import { type JSX } from 'react';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import { categoryRailStore } from '@services/CategoryRailStore';
import { searchService } from '@services/SearchService';
import CategoryRail from '@components/NoteSidebar/CategoryRail';
import { ImgMenu } from '@components/Images/ImgMenu';
import { ImgTimes } from '@components/Images/ImgTimes';

/**
 * Always-mounted category rail (AppShell, above <Routes>), persisting across
 * Dashboard and Notes routes so order never re-sorts. Mode-aware:
 *  - dashboard: navigates
 *  - notes:     defers to the Notes page's state + drag/edit handlers
 */
function SharedCategoryRail(): JSX.Element {
  const store = categoryRailStore;
  const navigate = useNavigate();

  const isNotesMode = store.mode === 'notes';
  const ns = store.notesState;
  const handlers = store.notesHandlers;

  // Desktop: hide the rail (persisted); mobile dashboard: close the rail drawer.
  const onCollapse = (): void => {
    if (window.innerWidth <= 860 && !isNotesMode) {
      store.toggleMobileRail();
    } else {
      store.toggleRail();
    }
  };

  // --- Dashboard-mode wiring (this component drives navigation) ---
  // "All notes" returns to the last-open note (if any) so the user's context is
  // preserved instead of jumping to the server's activeNoteId. The flag tells the
  // Notes view to open the "All notes" list (not the note's own category).
  const onAllClick = () => {
    store.requestAllNotes();
    navigate(store.lastNoteId ? '/n/' + store.lastNoteId : '/notes');
  };
  const onCategoryClick = (cat: { id: string }) => navigate('/notes?cat=' + cat.id);
  const onAddCategory = () => navigate('/notes');

  // "Dashboard" row → leave the rail and clear any active search.
  const onDashboardClick = (): void => searchService.clear();
  const onSearchClick = (): void => searchService.focus();

  const isSearchMode = isNotesMode ? !!ns?.isSearchMode : searchService.isActive;
  const searchQuery = isNotesMode ? (ns?.searchQuery ?? '') : searchService.query;
  const searchCount = isNotesMode ? (ns?.searchCount ?? 0) : 0;

  const activeCategoryId = isNotesMode ? (ns?.activeCategoryId ?? '') : '';
  const isArchive = isNotesMode ? (ns?.isArchive ?? false) : false;
  const noteCounts = isNotesMode ? (ns?.noteCounts ?? {}) : store.dashboardNoteCounts;
  const totalNoteCount = isNotesMode ? (ns?.totalNoteCount ?? 0) : store.dashboardTotalNoteCount;

  const onRailAllClick = isNotesMode ? (handlers?.onAllClick ?? onAllClick) : onAllClick;
  const onRailCategoryClick = isNotesMode ? (handlers?.onCategoryClick ?? onCategoryClick) : onCategoryClick;
  const onRailAddCategory = isNotesMode ? (handlers?.onAddCategory ?? onAddCategory) : onAddCategory;
  const onRailDragEnd = isNotesMode ? handlers?.onCategoryDragEnd : undefined;
  const onRailEdit = isNotesMode ? handlers?.onCategoryEdit : undefined;

  // Dashboard-mode mobile drawer chrome.
  const showDrawerChrome = !isNotesMode && store.isMobileRailOpen;

  return (
    <>
      {showDrawerChrome && (
        <div className="mobile-sidebar-overlay visible" onClick={() => store.setMobileRailOpen(false)} />
      )}

      <CategoryRail
        categories={store.categories}
        activeCategoryId={activeCategoryId}
        noteCounts={noteCounts}
        totalNoteCount={totalNoteCount}
        isArchive={isArchive}
        onAllClick={onRailAllClick}
        onCategoryClick={onRailCategoryClick}
        onAddCategory={onRailAddCategory}
        onCollapse={onCollapse}
        onCategoryDragEnd={onRailDragEnd}
        onCategoryEdit={onRailEdit}
        isLoading={store.isLoading}
        isSearchMode={isSearchMode}
        searchCount={searchCount}
        searchQuery={searchQuery}
        onDashboardClick={onDashboardClick}
        onSearchClick={onSearchClick}
      />

      {/* Desktop re-open handle (rail collapsed) — shared across both modes. */}
      {store.isRailHidden && (
        <button className="pane-handle" title="Show categories" aria-label="Show categories" onClick={() => store.toggleRail()}>
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
        </button>
      )}

      {/* Mobile drawer toggle — dashboard mode only (notes mode uses Notes' own). */}
      {!isNotesMode && (
        <button
          className="mobile-sidebar-toggle"
          onClick={() => store.toggleMobileRail()}
          title={store.isMobileRailOpen ? 'Close menu' : 'Open menu'}
        >
          {store.isMobileRailOpen ? <ImgTimes size={18} /> : <ImgMenu size={18} />}
        </button>
      )}
    </>
  );
}

export default observer(SharedCategoryRail);
